// Copied from inv-lambda-data-loader's src/lib/validate-tracs.ts (itself invenire-v2's
// src/lib/media-import/validate-tracs.ts), so an upload is checked in the browser by the rules the
// Lambda applies before it imports; keep them in sync.

/** Problems (and warnings) listed per file; any beyond this are only counted. */
const MAX_LISTED_PROBLEMS = 100;

const WHOLE_NUMBER_RE = /^-?\d+$/;

export type TracsValidation = {
  /** The first problems found, each led by where it is, e.g. `Media.MediaLabel: missing`. */
  problems: string[];
  problemCount: number;
  /** Values the loaders read only in part, e.g. `…Size: "-1 B" is read as -1`. They don't fail. */
  warnings: string[];
  warningCount: number;
  /** FileInfo entries across all sessions. */
  recordCount: number;
};

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const kindOf = (value: unknown) =>
  value === null
    ? 'null'
    : Array.isArray(value)
      ? 'list'
      : typeof value === 'string'
        ? 'text'
        : typeof value;

const quote = (value: string) =>
  JSON.stringify(value.length > 40 ? `${value.slice(0, 40)}…` : value);

/**
 * Checks a TRACS JSON export against everything the json-data-loader worker reads from it (the
 * same reads as inv-lambda-data-loader on AWS, which it was ported from), so a file that passes
 * doesn't fail while importing. Number fields are read with parseInt, as both loaders do: a value
 * it reads only in part ("-1 B", "12.5", "0\n") passes with a warning, and one it can't read
 * fails. A file's size, dates and restoration position may be blank (older TRACS exports leave
 * them empty when unknown), and its restoration position may be text such as "N/A" (disk-image
 * exports have none); a session's backup date can't be blank, since the loader builds the
 * session's id from it. Size and record-count limits differ between the two systems and are left
 * to the caller.
 */
export function validateTracsExport(text: string): TracsValidation {
  const problems: string[] = [];
  let problemCount = 0;
  const warnings: string[] = [];
  let warningCount = 0;
  let recordCount = 0;
  const result = (): TracsValidation => ({
    problems,
    problemCount,
    warnings,
    warningCount,
    recordCount,
  });
  const report = (problem: string) => {
    problemCount += 1;
    if (problems.length < MAX_LISTED_PROBLEMS) {
      problems.push(problem);
    }
  };
  const at = (path: string, message: string) => report(`${path}: ${message}`);
  const warnAt = (path: string, message: string) => {
    warningCount += 1;
    if (warnings.length < MAX_LISTED_PROBLEMS) {
      warnings.push(`${path}: ${message}`);
    }
  };

  const objectAt = (value: unknown, path: string): UnknownRecord | undefined => {
    if (isRecord(value)) {
      return value;
    }
    at(path, value === undefined ? 'missing' : `expected an object, got ${kindOf(value)}`);
    return undefined;
  };

  const listAt = (value: unknown, path: string): unknown[] => {
    if (Array.isArray(value)) {
      return value;
    }
    at(path, value === undefined ? 'missing' : `expected a list, got ${kindOf(value)}`);
    return [];
  };

  const textAt = (value: unknown, path: string, { required = false } = {}) => {
    if (typeof value === 'string' && (value || !required)) {
      return;
    }
    at(
      path,
      value === undefined || value === '' ? 'missing' : `expected text, got ${kindOf(value)}`
    );
  };

  const wholeNumberAt = (
    value: unknown,
    path: string,
    { blankAllowed = false, negativeIsUnknown = false } = {}
  ) => {
    if (value === '' && blankAllowed) {
      return;
    }
    if (value === undefined || value === '') {
      at(path, 'missing');
      return;
    }
    if (typeof value !== 'number' && typeof value !== 'string') {
      at(path, `expected a whole number, got ${kindOf(value)}`);
      return;
    }
    // parseInt takes the leading whole number and ignores the rest.
    const read = Number.parseInt(String(value), 10);
    const shown = typeof value === 'number' ? String(value) : quote(value);
    if (Number.isNaN(read)) {
      at(path, `${shown} is not a number`);
    } else if (negativeIsUnknown && read < 0) {
      warnAt(path, `${shown} is a negative size (unknown)`);
    } else if (
      typeof value === 'number' ? !Number.isInteger(value) : !WHOLE_NUMBER_RE.test(value)
    ) {
      warnAt(path, `${shown} is read as ${read}`);
    }
  };

  // Both loaders ignore anything before the first "{" (S3Client.getFile here,
  // minioService.getMediaFile in invenire-v2).
  if (!text.trim()) {
    report('File is empty');
    return result();
  }
  const start = text.indexOf('{');
  if (start === -1) {
    report('No JSON object found (the file has no "{")');
    return result();
  }

  let parsed: UnknownRecord;
  try {
    parsed = JSON.parse(text.slice(start));
  } catch (err) {
    report(`Invalid JSON: ${err instanceof Error ? err.message : String(err)}`);
    return result();
  }

  const media = objectAt(parsed.Media, 'Media');
  if (!media) {
    return result();
  }
  textAt(media.MediaLabel, 'Media.MediaLabel', { required: true });
  objectAt(media.MediaType, 'Media.MediaType');

  listAt(media.BackUpSession, 'Media.BackUpSession').forEach((value, s) => {
    const sessionPath = `Media.BackUpSession[${s}]`;
    const session = objectAt(value, sessionPath);
    if (!session) {
      return;
    }
    wholeNumberAt(session.BackupDate, `${sessionPath}.BackupDate`);

    const files = listAt(session.FileInfo, `${sessionPath}.FileInfo`);
    recordCount += files.length;
    files.forEach((value, f) => {
      const filePath = `${sessionPath}.FileInfo[${f}]`;
      const file = objectAt(value, filePath);
      if (!file) {
        return;
      }
      // Only a missing or null position breaks the loaders. Text such as "N/A" (disk-image
      // exports have no tape position) reads as no position.
      const position = file.RestorationConfig;
      if (position === undefined || position === null) {
        objectAt(position, `${filePath}.RestorationConfig`);
      } else if (isRecord(position)) {
        for (const key of ['Block', 'FileMark', 'BlockOffset']) {
          wholeNumberAt(position[key], `${filePath}.RestorationConfig.${key}`, {
            blankAllowed: true,
          });
        }
      }
      for (const key of ['FileName', 'FilePath', 'FileExtension']) {
        textAt(file[key], `${filePath}.${key}`);
      }
      for (const key of ['ModifyDate', 'AccessDate', 'CreateDate']) {
        wholeNumberAt(file[key], `${filePath}.${key}`, { blankAllowed: true });
      }
      // Some exports write an unknown size as "-1 B"; the loaders store it as unknown.
      wholeNumberAt(file.Size, `${filePath}.Size`, {
        blankAllowed: true,
        negativeIsUnknown: true,
      });
    });
  });

  return result();
}
